import { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import AuthContext from '../Context/AuthContext';
import Popup from './Popup';
import ButtonSolid from './ButtonSolid';
import ButtonOutline from './ButtonOutline';
import LoadingAnimation from './LoadingAnimation';
import { CloseIcon } from './Icons';
import { analyzeVideo, convertVideoForWeb } from '../utils/videoConvert';
import { replaceAsset } from '../utils/assetUpload';

const mb = (bytes) => `${(bytes / 1048576).toFixed(0)} MB`;

const STATUS_LABEL = {
    ok: 'Already optimized',
    needs: 'Needs optimizing',
    blocked: "Can't optimize here",
    converting: 'Converting...',
    uploading: 'Uploading...',
    done: 'Done',
    failed: 'Failed',
    skipped: 'Skipped',
};

// Converts one video to a web-friendly MP4 in the browser and replaces the stored file
// (same asset, so every assembly keeps working). Checks the video first and only
// offers to convert it when it needs it.
function OptimizeVideoModal({ asset, onClose, onChanged }) {
    const { token, logout } = useContext(AuthContext);
    const [scanning, setScanning] = useState(true);
    const [running, setRunning] = useState(false);
    const [row, setRow] = useState({ status: 'needs', detail: '', progress: 0, sizeBytes: 0 });
    const abortRef = useRef(null);
    const changedRef = useRef(false);

    const patch = (changes) => setRow((current) => ({ ...current, ...changes }));

    useEffect(() => {
        let cancelled = false;
        const scan = async () => {
            try {
                const info = await analyzeVideo({ url: asset.source, name: asset.name });
                if (cancelled) return;
                if (info.needsConversion && info.blocker) {
                    // This browser can't do the conversion; another one (Chrome/Edge) might.
                    setRow({ status: 'blocked', detail: `Can't be optimized in this browser: ${info.blocker}. Use Chrome or Edge. (${info.reasons.join(', ')} · ${mb(info.sizeBytes)})` });
                } else if (info.needsConversion) {
                    setRow({ status: 'needs', detail: `${info.reasons.join(', ')} · ${mb(info.sizeBytes)}`, sizeBytes: info.sizeBytes });
                } else {
                    setRow({ status: 'ok', detail: `${info.width}x${info.height} · ${info.mbps.toFixed(1)} Mbps · ${mb(info.sizeBytes)}` });
                }
            } catch (error) {
                if (!cancelled) setRow({ status: 'failed', detail: `Can't read this video: ${error.message}` });
            } finally {
                if (!cancelled) setScanning(false);
            }
        };
        scan();
        return () => { cancelled = true; };
    }, [asset.source, asset.name]);

    const optimize = async () => {
        const controller = new AbortController();
        abortRef.current = controller;
        setRunning(true);
        try {
            patch({ status: 'converting', progress: 0, attempted: false });
            const converted = await convertVideoForWeb({
                url: asset.source,
                name: asset.name,
                signal: controller.signal,
                onProgress: (progress) => patch({ progress }),
            });
            patch({ status: 'uploading', progress: 0 });
            const result = await replaceAsset({
                file: converted,
                assetId: asset._id,
                type: asset.type,
                token,
                signal: controller.signal,
                skipConversion: true,
                onProgress: (progress) => patch({ progress }),
            });
            if (result.duplicate) {
                patch({ status: 'skipped', detail: `The converted file is identical to "${result.asset.name}"` });
            } else {
                changedRef.current = true;
                patch({ status: 'done', detail: `${mb(row.sizeBytes)} → ${mb(converted.size)}` });
            }
        } catch (error) {
            if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
                patch({ status: 'needs' }); // stopped: the video is untouched
            } else {
                if (error.response?.status === 401) logout();
                patch({ status: 'failed', attempted: true, detail: error.response?.data?.message || error.message || 'Failed' });
            }
        } finally {
            abortRef.current = null;
            setRunning(false);
        }
    };

    const close = () => {
        abortRef.current?.abort();
        if (changedRef.current) onChanged?.();
        onClose();
    };

    const { status, detail, progress } = row;
    const canOptimize = !running && (status === 'needs' || (status === 'failed' && row.attempted));

    return (
        <Popup maxWidth='md:max-w-[560px]'>
            <div className='p-2'>
                <div className='flex justify-between items-center mb-3'>
                    <p className='text-lg font-semibold text-gray-700'>Optimize Video</p>
                    <button
                        type='button'
                        onClick={close}
                        title={running ? 'Stop and close' : 'Close'}
                        aria-label='Close'
                        className='p-2 border-2 bg-white border-gray-300 rounded-md cursor-pointer'
                    >
                        <CloseIcon size='w-5 h-5' />
                    </button>
                </div>
                <p className='text-sm text-gray-500 mb-4'>
                    Converts this video to a web-friendly MP4 (H.264 video, AAC audio) that plays in every browser.
                    Anything above 1080p is scaled down to 1080p; smaller videos keep their size.
                    The original file is replaced. Keep this window open while it runs.
                </p>

                {scanning ? (
                    <div className='py-6'>
                        <LoadingAnimation loadingText='Checking video' />
                    </div>
                ) : (
                    <>
                        <div className='border border-slate-200 rounded-md px-3 py-2'>
                            <div className='flex justify-between items-start gap-3'>
                                <p className='min-w-0 break-words text-sm text-gray-700'>{asset.name}</p>
                                <span className={`shrink-0 text-xs font-medium ${status === 'done' || status === 'ok' ? 'text-green-600' : status === 'failed' ? 'text-red-600' : status === 'needs' ? 'text-orange-500' : 'text-gray-500'}`}>
                                    {STATUS_LABEL[status]}
                                </span>
                            </div>
                            {(status === 'converting' || status === 'uploading') && (
                                <div className='w-full bg-gray-200 rounded-full h-1.5 mt-2'>
                                    <div className='bg-orange-400 h-1.5 rounded-full transition-all' style={{ width: `${progress || 0}%` }} />
                                </div>
                            )}
                            {detail && <p className='text-xs text-gray-400 mt-1 break-words'>{detail}</p>}
                        </div>

                        <div className='flex flex-col md:flex-row-reverse gap-4 mt-4'>
                            {running ? (
                                <ButtonOutline buttonText='Stop' onClick={() => abortRef.current?.abort()} className='w-full' />
                            ) : (
                                <>
                                    {canOptimize && <ButtonSolid buttonText='Optimize' onClick={optimize} className='w-full' />}
                                    <ButtonOutline buttonText='Close' onClick={close} className='w-full' />
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </Popup>
    );
}

export default OptimizeVideoModal;
